import axios from "axios";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "react-toastify";
import Papa from "papaparse";
import { FaDownload } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const PendingKyc = () => {
  const [pendingList, setPendingList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDocFilter, setSelectedDocFilter] = useState("all");
  const [selectedLocation, setSelectedLocation] = useState("all");
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  useEffect(() => {
    fetchPendingKyc();
  }, []);

  const fetchPendingKyc = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/supervisor/users`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const pending = Array.isArray(data.users)
        ? data.users.filter(
            (user) =>
              !user.kyc ||
              !user.pan_number ||
              !user.aadhar_number ||
              !user.account_number
          )
        : [];

      setPendingList(pending);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Unable to load pending KYC list"
      );
    } finally {
      setLoading(false);
    }
  };

  const filteredList = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    if (!query) {
      return pendingList;
    }

    return pendingList.filter((user) => {
      const name = user.full_Name?.toLowerCase() || "";
      const id = user.employeeId?.toString() || "";
      const phone = user.contact || "";
      return (
        name.includes(query) ||
        id.includes(query) ||
        phone.includes(query)
      );
    });
  }, [pendingList, searchTerm]);

  const formatLocation = useCallback(
    (user) => [user.dist, user.state, user.country].filter(Boolean).join(", "),
    []
  );

  const displayList = useMemo(() => {
    if (selectedDocFilter === "all") return filteredList;

    return filteredList.filter((user) => {
      if (selectedDocFilter === "pan") return !user.pan_number;
      if (selectedDocFilter === "aadhar") return !user.aadhar_number;
      if (selectedDocFilter === "bank") return !user.account_number;
      return true;
    });
  }, [filteredList, selectedDocFilter]);

  const locationOptions = useMemo(() => {
    const unique = new Set();
    pendingList.forEach((user) => {
      const location = formatLocation(user);
      unique.add(location || "Unknown location");
    });
    return ["all", ...Array.from(unique)];
  }, [pendingList, formatLocation]);

  useEffect(() => {
    if (
      selectedLocation !== "all" &&
      !locationOptions.includes(selectedLocation)
    ) {
      setSelectedLocation("all");
    }
  }, [locationOptions, selectedLocation]);

  const locationFilteredList = useMemo(() => {
    if (selectedLocation === "all") return displayList;
    return displayList.filter(
      (user) => formatLocation(user) === selectedLocation
    );
  }, [displayList, selectedLocation, formatLocation]);

  const aadharPendingList = useMemo(
    () => locationFilteredList.filter((user) => !user.aadhar_number),
    [locationFilteredList]
  );

  const digilockerPendingList = useMemo(
    () => locationFilteredList.filter((user) => !user.kyc),
    [locationFilteredList]
  );

  const downloadCSV = () => {
    if (!locationFilteredList.length) {
      toast.info("No employees to download for the selected filter.");
      return;
    }

    const csvData = locationFilteredList.map((user) => ({
      "Employee ID": user.employeeId ? `CMPL${user.employeeId}` : "—",
      Name: user.full_Name ?? "—",
      Contact: user.contact ?? "—",
      Email: user.email ?? "—",
      Location: formatLocation(user) || "—",
      "KYC Status": user.kyc ? "Verified" : "Pending",
      "Missing PAN": user.pan_number ? "No" : "Yes",
      "Missing Aadhar": user.aadhar_number ? "No" : "Yes",
      "Missing Bank": user.account_number ? "No" : "Yes",
    }));

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `pending-docs-${selectedDocFilter}-${selectedLocation}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3 py-md-4">
      <div className="row align-items-center mb-3">
        <div className="col-12 col-md-8">
          <h1 className="h3 mb-1 text-primary fw-bold">Pending KYC</h1>
          <p className="text-muted mb-0">
            Employees missing PAN, Aadhar, or bank account data.
          </p>
        </div>
        <div className="col-12 col-md-4 mt-3 mt-md-0">
          <div className="input-group shadow-sm">
            <span className="input-group-text bg-white border-end-0">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control border-start-0"
              placeholder="Search by name, ID or phone"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ boxShadow: "none" }}
            />
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12 col-lg-4 mb-3 mb-lg-0">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <p className="text-uppercase text-muted mb-1 small">Total Missing Docs</p>
              <h3 className="mb-0">{filteredList.length}</h3>
              <p className="mb-0 text-muted">
                {pendingList.length === filteredList.length
                  ? "Showing all employees who need to update documents."
                  : `${filteredList.length} filtered results.`}
              </p>
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-8">
          <div className="row g-3">
            <div className="col-12 col-md-4">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <p className="text-muted mb-1 small">Missing PAN</p>
                  <h4 className="mb-0">
                    {
                      pendingList.filter((user) => !user.pan_number).length
                    }
                  </h4>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-4">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <p className="text-muted mb-1 small">Missing Aadhar</p>
                  <h4 className="mb-0">
                    {
                      pendingList.filter((user) => !user.aadhar_number).length
                    }
                  </h4>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-4">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <p className="text-muted mb-1 small">Missing Bank</p>
                  <h4 className="mb-0">
                    {
                      pendingList.filter((user) => !user.account_number).length
                    }
                  </h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="row mb-3 align-items-center">
        <div className="col-12 col-lg-9 mb-2 mb-lg-0">
          <div className="btn-group w-100 gap-2 flex-wrap" role="group" aria-label="Document filter">
            {[
              { key: "all", label: "All Missing Docs" },
              { key: "pan", label: "Missing PAN" },
              { key: "aadhar", label: "Missing Aadhar" },
              { key: "bank", label: "Missing Bank" },
            ].map((filter) => (
              <button
                key={filter.key}
                type="button"
                className={`btn flex-grow-1 text-start ${
                  selectedDocFilter === filter.key
                    ? "btn-primary"
                    : "btn-outline-primary"
                }`}
                onClick={() => setSelectedDocFilter(filter.key)}
              >
                {filter.label}
                <span className="d-block small text-muted">
                  {
                    pendingList.filter((user) => {
                      if (filter.key === "all") return true;
                      if (filter.key === "pan") return !user.pan_number;
                      if (filter.key === "aadhar") return !user.aadhar_number;
                      if (filter.key === "bank") return !user.account_number;
                      return false;
                    }).length
                  }{" "}
                  employees
                </span>
              </button>
            ))}
          </div>
        </div>
        <div className="col-12 col-lg-3">
          <div className="d-flex flex-column flex-lg-row gap-2 justify-content-lg-end">
            <select
              className="form-select form-select-sm"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
            >
              {locationOptions.map((location) => (
                <option key={location} value={location}>
                  {location === "all" ? "All locations" : location}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-outline-success"
              onClick={downloadCSV}
            >
              <FaDownload className="me-2" />
              Export List
            </button>
          </div>
        </div>
      </div>


      <div className="card shadow-sm border-0">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Employee ID</th>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Email</th>
                  <th>Pending Items</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {locationFilteredList.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-4">
                      No pending KYC found for the current criteria.
                    </td>
                  </tr>
                )}

                {locationFilteredList.map((user, index) => {
                  const pendingItems = [];
                  if (!user.kyc) pendingItems.push("KYC Verification Pending");
                  if (!user.pan_number) pendingItems.push("PAN Card Pending");
                  if (!user.aadhar_number) pendingItems.push("Aadhaar Card Pending");
                  if (!user.account_number) pendingItems.push("Bank Account Pending");

                  return (
                    <tr key={`${user.employeeId}-${index}`} className="align-middle">
                      <td>{user.employeeId ? `CMPL${user.employeeId}` : "—"}</td>
                      <td>{user.full_Name || "—"}</td>
                      <td>{user.contact || "—"}</td>
                  <td>{user.email || "—"}</td>
                  <td>
                    <div className="d-flex flex-wrap gap-1">
                      {pendingItems.length > 0
                        ? pendingItems.map((item, idx) => (
                            <span key={idx} className="badge bg-warning text-dark">
                              {item}
                            </span>
                          ))
                        : <span className="badge bg-success">All documents complete</span>
                      }
                    </div>
                  </td>
                  <td>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() =>
                        navigate("/supervisor_dashboard/employee-detail", {
                          state: { employeeId: user._id },
                        })
                      }
                    >
                      View Profile
                    </button>
                  </td>
                </tr>
              );
            })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="row mt-4">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header bg-white">
              <div className="d-flex align-items-center justify-content-between">
                <h5 className="mb-0">Aadhar Pending</h5>
                <span className="badge bg-danger">
                  {aadharPendingList.length} employees
                </span>
              </div>
              <p className="mb-0 text-muted">
                Showing employees who still need to upload their Aadhaar.
              </p>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="text-center">#</th>
                      <th>Employee ID</th>
                      <th>Name</th>
                      <th>Contact</th>
                      <th>Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aadharPendingList.length === 0 && (
                      <tr>
                        <td colSpan="5" className="text-center text-muted py-3">
                          No employees without Aadhaar on file.
                        </td>
                      </tr>
                    )}
                    {aadharPendingList.map((user, index) => (
                      <tr key={`aadhar-${user._id || index}`}>
                        <td className="text-center">{index + 1}</td>
                        <td>{user.employeeId ? `CMPL${user.employeeId}` : "—"}</td>
                        <td>{user.full_Name || "—"}</td>
                        <td>{user.contact || "—"}</td>
                        <td>
                          {[user.dist, user.state, user.country]
                            .filter(Boolean)
                            .join(", ") || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="row mt-4">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header bg-white">
              <div className="d-flex align-items-center justify-content-between">
                <h5 className="mb-0">DigiLocker KYC Pending</h5>
                <span className="badge bg-danger">
                  {digilockerPendingList.length} employees
                </span>
              </div>
              <p className="mb-0 text-muted">
                Employees who have not completed DigiLocker Aadhaar verification.
              </p>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="text-center">#</th>
                      <th>Employee ID</th>
                      <th>Name</th>
                      <th>Contact</th>
                      <th>Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {digilockerPendingList.length === 0 && (
                      <tr>
                        <td colSpan="5" className="text-center text-muted py-3">
                          All filtered employees have completed DigiLocker KYC.
                        </td>
                      </tr>
                    )}
                    {digilockerPendingList.map((user, index) => (
                      <tr key={`digilocker-${user._id || index}`}>
                        <td className="text-center">{index + 1}</td>
                        <td>{user.employeeId ? `CMPL${user.employeeId}` : "—"}</td>
                        <td>{user.full_Name || "—"}</td>
                        <td>{user.contact || "—"}</td>
                        <td>
                          {[user.dist, user.state, user.country]
                            .filter(Boolean)
                            .join(", ") || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingKyc;
